import { useEffect, useState } from "react";
import { API } from "@/lib/api";
import { formatINR } from "@/lib/format";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, Download, Filter, TrendingDown } from "lucide-react";
import { useBranch } from "@/context/BranchContext";

export default function MinGuaranteeReport() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { branchId } = useBranch();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(`${API}/reports/min-guarantee`, { params });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, branchId]);

  const rows = data?.per_delivery_boy || [];
  const totals = data?.totals || {};

  const clear = () => {
    setStartDate("");
    setEndDate("");
  };

  const csvHref = (() => {
    const q = new URLSearchParams();
    if (startDate) q.set("start_date", startDate);
    if (endDate) q.set("end_date", endDate);
    return `${API}/reports/export.csv${q.toString() ? "?" + q.toString() : ""}`;
  })();

  return (
    <div className="space-y-6" data-testid="min-guarantee-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">Subsidy Tracker</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-1">
            Minimum Guarantee Loss
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            How much the company paid on top of actual earnings to meet each delivery boy&apos;s guarantee.
            <span className="ml-1 tabular text-zinc-700">Loss = Minimum Guarantee − Earning</span> (per day, only when earning falls short).
          </p>
        </div>
        <a href={csvHref} target="_blank" rel="noreferrer">
          <Button variant="outline" className="rounded-lg" data-testid="mg-export-csv-btn">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </a>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-zinc-200 p-4 flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 pr-1">
          <Filter className="h-3.5 w-3.5" /> Period
        </div>
        <div className="space-y-1">
          <label className="text-xs text-zinc-500">From</label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-40"
            data-testid="mg-filter-start-date"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-zinc-500">To</label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-40"
            data-testid="mg-filter-end-date"
          />
        </div>
        {(startDate || endDate) && (
          <Button variant="ghost" onClick={clear} data-testid="mg-clear-filters">
            Clear
          </Button>
        )}
        <div className="ml-auto text-xs text-zinc-500">
          {startDate || endDate ? "Filtered range" : "All time"}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total Loss (Subsidy Paid)"
          value={formatINR(totals.total_loss || 0)}
          sub="Company paid on top of earnings"
          tone="danger"
          icon={TrendingDown}
          testId="mg-kpi-total-loss"
        />
        <KpiCard
          label="Shortfall Days"
          value={totals.shortfall_days || 0}
          sub="Days below guarantee"
          testId="mg-kpi-shortfall-days"
        />
        <KpiCard
          label="Total Earning"
          value={formatINR(totals.total_earning || 0)}
          sub="Actual earnings recorded"
          tone="positive"
          testId="mg-kpi-total-earning"
        />
        <KpiCard
          label="Total Guarantee"
          value={formatINR(totals.total_expected_guarantee || 0)}
          sub="Sum of daily guarantees"
          testId="mg-kpi-total-guarantee"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              <Th>Delivery Boy</Th>
              <Th className="text-right">Min. Guarantee</Th>
              <Th className="text-right">Entries</Th>
              <Th className="text-right">Shortfall Days</Th>
              <Th className="text-right">Total Earning</Th>
              <Th className="text-right">Total Guarantee</Th>
              <Th className="text-right">Loss (Subsidy)</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="text-center py-8 text-zinc-500">Loading…</td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-14">
                  <div className="inline-flex flex-col items-center gap-2 text-zinc-500">
                    <ShieldAlert className="h-8 w-8 text-zinc-300" />
                    <div className="font-medium text-zinc-700">No entries in this period</div>
                    <div className="text-xs">Add collection entries to see min guarantee loss.</div>
                  </div>
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.delivery_boy_id}
                className="border-b border-zinc-100 hover:bg-zinc-50 tabular"
                data-testid={`mg-row-${r.delivery_boy_id}`}
              >
                <td className="px-4 py-3 font-medium text-zinc-900">
                  {r.delivery_boy_name || "—"}
                  {r.total_loss > 0 && (
                    <Badge className="ml-2 bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-50">
                      Subsidised
                    </Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-right">{formatINR(r.min_guarantee)}</td>
                <td className="px-4 py-3 text-right">{r.entries}</td>
                <td className={`px-4 py-3 text-right ${r.shortfall_days > 0 ? "text-rose-600 font-medium" : "text-zinc-500"}`}>
                  {r.shortfall_days}
                </td>
                <td className="px-4 py-3 text-right text-emerald-700">{formatINR(r.total_earning)}</td>
                <td className="px-4 py-3 text-right">{formatINR(r.total_expected_guarantee)}</td>
                <td className={`px-4 py-3 text-right font-semibold ${r.total_loss > 0 ? "text-rose-600" : "text-zinc-500"}`}>
                  {formatINR(r.total_loss)}
                </td>
              </tr>
            ))}
            {rows.length > 0 && (
              <tr className="bg-zinc-50 tabular font-medium">
                <td className="px-4 py-3">Total</td>
                <td className="px-4 py-3 text-right text-zinc-500">—</td>
                <td className="px-4 py-3 text-right">{totals.entries}</td>
                <td className="px-4 py-3 text-right text-rose-600">{totals.shortfall_days}</td>
                <td className="px-4 py-3 text-right text-emerald-700">{formatINR(totals.total_earning)}</td>
                <td className="px-4 py-3 text-right">{formatINR(totals.total_expected_guarantee)}</td>
                <td className="px-4 py-3 text-right text-rose-600 font-semibold">{formatINR(totals.total_loss)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 ${className}`}>
    {children}
  </th>
);

const KpiCard = ({ label, value, sub, tone = "default", icon: Icon, testId }) => {
  const cls = {
    default: "text-zinc-950",
    positive: "text-emerald-700",
    danger: "text-rose-600",
  }[tone];
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-zinc-400" />}
      </div>
      <div className={`mt-3 font-display text-3xl font-semibold tabular ${cls}`}>{value}</div>
      {sub && <div className="mt-2 text-xs text-zinc-500">{sub}</div>}
    </div>
  );
};
